import React, { useEffect, useState, useMemo } from 'react'
import Image from 'next/image';
import { motion } from 'framer-motion';
import { useDeviceInfo } from '@/hooks/useIsMobile';
import { useGame } from '@/app/providers/GameContextProvider';
import { Expansion } from '@/lib/types/common/Expansion';

interface Star {
    id: number
    x: number // percentage 0-100
    y: number // percentage 0-100
    size: number
    opacity: number
    twinkleDuration: number
    twinkleDelay: number
}

const BackgroundEther = () => {
    const { gameState } = useGame();
    const [isClient, setIsClient] = useState(false)
    const { isMobile } = useDeviceInfo()


    const selectedExpansions = Array.from(gameState?.selectedExpansions || []);
    const hueRotate = selectedExpansions.length === 1 &&
        selectedExpansions[0] === Expansion.ShB ? 'hue-rotate-[40deg] brightness-[60%] saturate-[1.1] contrast-[1.1]' : ''
    
    // Generate stars only once using useMemo - positions are percentages for responsiveness
    const stars = useMemo<Star[]>(() => {
        if (isMobile) return [] // No stars on mobile for performance
        const generatedStars: Star[] = []
        for (let i = 0; i < 800; i++) {
            generatedStars.push({
                id: i,
                x: Math.random() * 100, // percentage
                y: Math.random() * 100, // percentage
                size: Math.random() * 2 + 2,
                opacity: Math.random() * 0.1 + 0.9,
                twinkleDuration: 2 + Math.random() * 3, // 2-5 seconds
                twinkleDelay: Math.random() * 1, // 0-1 seconds delay
            })
        }
        return generatedStars
    }, [])

    useEffect(() => {
        setIsClient(true)
    }, [])

    return (
        <motion.div
            initial={{ opacity: 1 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className={`fixed inset-0 overflow-hidden pointer-events-none duration-[700ms] ${hueRotate}`}
        >

            {/* Deep space background */}
            <Image
                src="/etheral_background.webp"
                alt="Ether Background"
                layout="fill"
                objectFit="cover"
                quality={100}
            />

            {/* Background stars - CSS animated, no JS updates */}
            {isClient && !isMobile && stars.map(star => (
                <div
                    key={`star-${star.id}`}
                    className="absolute rounded-full bg-blue-200 star-twinkle"
                    style={{
                        left: `${star.x}%`,
                        top: `${star.y}%`,
                        width: star.size,
                        height: star.size,
                        '--star-opacity': star.opacity,
                        '--twinkle-duration': `${star.twinkleDuration}s`,
                        '--twinkle-delay': `${star.twinkleDelay}s`,
                        boxShadow: `0 0 ${star.size * 3}px rgba(147, 197, 253, ${star.opacity * 0.7})`,
                    } as React.CSSProperties}
                />
            ))}

            <style jsx>{`
                .star-twinkle {
                    opacity: var(--star-opacity);
                    animation: twinkle var(--twinkle-duration) ease-in-out var(--twinkle-delay) infinite;
                    will-change: opacity;
                }

                @keyframes twinkle {
                    0%, 100% { opacity: var(--star-opacity); }
                    50% { opacity: calc(var(--star-opacity) * 0.3); }
                }
            `}</style>
        </motion.div>
    )
}

export default BackgroundEther